import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * VH-161 — copy one logged meal onto another day.
 *
 * VH-141 built copying as a TARGET-first action: an empty slot on today offered
 * to pull the most recent matching meal forward. That only appears when the slot
 * is empty, so once a day is fully logged there is no copy affordance anywhere —
 * reported from production as "I cannot find the button".
 *
 * This is the SOURCE-first direction VH-141 left as an open question: start from
 * a meal you already logged and send it to a day you choose. Reto eats the same
 * thing most of the week, so "this lunch, again on Thursday" is the actual
 * intent.
 *
 * The server owns the rules and this dialog does not duplicate them, it only
 * keeps the picker from offering a date the server would certainly reject:
 * copying runs FORWARD only (`source_date >= log_date` is a 400), and a target
 * day that already holds this meal type is refused with 409 rather than
 * duplicated.
 */
export default function CopyMealToDateDialog({
  mealType,
  sourceDate,
  onClose,
  onCopy,
}) {
  const { t } = useTranslation()

  const source = (sourceDate || '').slice(0, 10)

  // Local YYYY-MM-DD. NEVER toISOString(): that converts local midnight to UTC,
  // so anywhere east of Greenwich the date silently walks back a day — it made
  // the earliest legal target the source date itself, the one value the server
  // is guaranteed to reject with a 400.
  const isoLocal = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  // VH-181: no minimum any more. The forward-only rule belonged to the BULK
  // endpoint (`/meals/copy`, VH-141); copying a single meal has no such
  // constraint, so restricting the picker would forbid something the server
  // accepts — including copying onto the same day, which is simply "again".
  const minTarget = undefined

  const todayIso = isoLocal(new Date())

  // Default to today when today is a legal target, otherwise the earliest one.
  const [targetDate, setTargetDate] = useState(
    minTarget && todayIso < minTarget ? minTarget : todayIso
  )
  const [isCopying, setIsCopying] = useState(false)
  const [error, setError] = useState(null)

  const handleCopy = async () => {
    if (!targetDate || isCopying) return
    setIsCopying(true)
    setError(null)
    try {
      await onCopy(targetDate)
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, t('common.error', 'An error occurred')))
    } finally {
      setIsCopying(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-surface-container rounded-lg shadow-xl max-w-sm w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-900">
            {t('nutrition.copyMealTitle', 'Copy this meal')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title={t('common.close', 'Close')}
            data-testid="copy-meal-close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 flex-1 min-h-0 overflow-y-auto space-y-4">
          <p className="text-sm text-gray-500">
            {t('nutrition.copyMealFrom', {
              mealType: mealType ? mealType.charAt(0) + mealType.slice(1).toLowerCase() : '',
              date: source,
              defaultValue: 'Copying {{mealType}} from {{date}}',
            })}
          </p>

          <div>
            <label className="form-label" htmlFor="copy-meal-target-date">
              {t('nutrition.copyMealToDate', 'Copy to date')}
            </label>
            <input
              id="copy-meal-target-date"
              type="date"
              value={targetDate}
              min={minTarget}
              onChange={(e) => { setTargetDate(e.target.value); setError(null) }}
              className="form-input"
              data-testid="copy-meal-date"
            />

          </div>

          {error && (
            <div className="text-red-600 text-sm" data-testid="copy-meal-error">{error}</div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <button onClick={onClose} className="btn-outline" data-testid="copy-meal-cancel">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleCopy}
            disabled={!targetDate || isCopying}
            className="btn-primary"
            data-testid="copy-meal-confirm"
          >
            {isCopying ? t('common.saving', 'Saving...') : t('nutrition.copyMealConfirm', 'Copy')}
          </button>
        </div>
      </div>
    </div>
  )
}
